// The one cached shape for ['me','connections'] (issue: key registry
// split -- see lib/query-keys.ts). The cache holds an infinite-query
// pages object; these pure helpers are how flat-array consumers (the
// badge counts, the conversation header, the reveal-requests sections)
// read it, so neither shape has a second home.
import type { InfiniteData } from '@tanstack/react-query';

import type { Connection, ConnectionsPage } from '@/lib/api';

export function selectAllConnections(pages: ConnectionsPage[] | undefined): Connection[] {
  return pages?.flatMap((page) => page.items) ?? [];
}

// True when the loaded pages reach the end of the cursor chain
// (nextCursor is '' on the last page). Flat consumers wait for this
// before they see data -- the same complete-list-or-nothing contract
// the old fetchAllConnections queryFn had, where any page failure
// meant no data rather than a truncated list.
export function isCompleteConnectionsPages(pages: ConnectionsPage[] | undefined): boolean {
  if (!pages) return false;

  const last = pages[pages.length - 1];

  return last !== undefined && !last.nextCursor;
}

// Optimistic mute toggle: patches one connection's `muted` flag inside
// the cached infinite-query pages, immutably (undefined passes through
// so it can be handed straight to setQueryData).
export function setConnectionMuted(
  data: InfiniteData<ConnectionsPage> | undefined,
  connectionId: string,
  muted: boolean,
): InfiniteData<ConnectionsPage> | undefined {
  if (!data) return data;

  return {
    ...data,
    pages: data.pages.map((p) => ({
      ...p,
      items: p.items.map((c) => (c.id === connectionId ? { ...c, muted } : c)),
    })),
  };
}
